<template>
  <div class="device_container">
    <MessageTop class="device_message_top" />

    <div class="device">
      <!-- 虚拟键盘选键提示 (Simplified V2) -->
      <Transition name="fade-slide">
        <div class="mgx_selection_guide" v-if="showSelectionGuide">
          <div class="guide_content_card">
            <div class="animation_area">
              <!-- 点选演示 -->
              <div class="demo_item">
                <div class="demo_visual">
                  <div class="demo_key is_animated"></div>
                  <div class="demo_hand click_anim">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10 11V6a2 2 0 0 1 4 0v7"></path>
                      <path d="M18 11V9a2 2 0 0 0-4 0v2"></path>
                      <path d="M10 11V9a2 2 0 0 0-4 0v2"></path>
                      <path d="M10 15c0 4 8 4 8 0v-4"></path>
                      <path d="M2 13c0 4 4 4 4 0v-2"></path>
                    </svg>
                  </div>
                </div>
                <div class="demo_label">{{ $t('guide_tp_click') }}</div>
              </div>
              <div class="demo_divider"></div>
              <!-- 连选演示 -->
              <div class="demo_item">
                <div class="demo_visual">
                  <div class="demo_keys">
                    <div class="mini_key"></div>
                    <div class="mini_key"></div>
                    <div class="mini_key"></div>
                  </div>
                  <div class="demo_hand drag_anim">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10 11V6a2 2 0 0 1 4 0v7"></path>
                      <path d="M18 11V9a2 2 0 0 0-4 0v2"></path>
                      <path d="M10 11V9a2 2 0 0 0-4 0v2"></path>
                      <path d="M10 15c0 4 8 4 8 0v-4"></path>
                      <path d="M2 13c0 4 4 4 4 0v-2"></path>
                    </svg>
                  </div>
                </div>
                <div class="demo_label">{{ $t('guide_tp_box') }}</div>
              </div>
            </div>
            <div class="text_area">
              <div class="main_tip">{{ $t('guide_tp_main_tip') }}</div>
            </div>
          </div>
        </div>
      </Transition>
      <Decorate v-if="isShowDecorate" />
      <Device_set v-else />
      <Power class="device_power" v-if="isShowPower" />
    </div>

    <MessageBottom class="device_message_bottom" />
  </div>
</template>

<script setup lang="ts">
import Device_set from './device_set.vue'
import Power from '../power/power.vue'
import MessageTop from '../message/message_top.vue'
import MessageBottom from '../message/message_bottom.vue'
import { computed, ref, watch } from 'vue'
import { operate, OperateType } from '@/service/driver/operate'
import { DeviceManager } from '@/service/device'
import { speciaBehiver, specialCheck } from '@/define/limit'
import Decorate from './decorate.vue'
import { lightSet } from '@/service/driver/light_set'
import { LightModeType } from '@/define/light'
import { magnetSet } from '@/service/driver/magnet_set'

const showSelectionGuide = ref(false)

// 监听磁轴设置的引导标志位
watch(
  () => (magnetSet as any)._showGuideFlag?.value,
  (val) => {
    if (val) {
      showSelectionGuide.value = true
      setTimeout(() => {
        showSelectionGuide.value = false
        if ((magnetSet as any)._showGuideFlag) (magnetSet as any)._showGuideFlag.value = false
      }, 2000)
    }
  },
)

// 实时监听选键状态：一旦用户选中了按键（单击或框选），立即隐藏引导动画
watch(
  () => magnetSet.selectedKeys.value.length,
  (newCount) => {
    if (newCount > 0 && showSelectionGuide.value) {
      showSelectionGuide.value = false
      // 同时重置 Service 里的标志位，防止逻辑残留
      if ((magnetSet as any)._showGuideFlag) (magnetSet as any)._showGuideFlag.value = false
    }
  },
)

const isShowDecorate = computed(() => {
  return lightSet.currentModeTypeTab.value == LightModeType.Side && lightSet.modeName.includes('自定义')
})

const isShowPower = computed(() => {
  return specialCheck(speciaBehiver.ShowBattery, DeviceManager.instance.state.currentPid) && DeviceManager.instance.state.deviceStatus.IsConnect
})

watch(operate.getActionType(), (_type: OperateType) => {
  // 如果切换出触发设置页面，立即强制隐藏引导动画
  if (_type !== OperateType.mgx) {
    showSelectionGuide.value = false
    if ((magnetSet as any)._showGuideFlag) (magnetSet as any)._showGuideFlag.value = false
  }
})
</script>

<style lang="less">
.device_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  position: relative;
  padding: 8px 0;
  gap: 12px;
  overflow: visible;

  .device_message_top,
  .device_message_bottom {
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 300;
    pointer-events: none;
  }

  .device_message_top {
    top: 0;
  }

  .device_message_bottom {
    bottom: 0;
  }

  .device {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: max-content;
    max-width: 100%;
    flex: 0 1 auto;
    position: relative;

    .device_power {
      position: absolute;
      top: 0;
      right: -70px;
    }

    /* 虚拟键盘选键提示 (Simplified V2 Design) */
    .mgx_selection_guide {
      position: absolute;
      top: 55%;
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: center;
      pointer-events: none;

      .guide_content_card {
        padding: 24px 32px;
        background: var(--glass-panel-bg);
        backdrop-filter: blur(40px) saturate(180%);
        border-radius: var(--radius-l);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
        border: 1px solid rgba(255, 255, 255, 0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;

        .animation_area {
          display: flex;
          align-items: center;
          gap: 32px;

          .demo_divider {
            width: 1px;
            height: 40px;
            background: rgba(0, 0, 0, 0.05);
          }

          .demo_item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;

            .demo_label {
              font-size: 12px;
              font-weight: 600;
              color: var(--color-text-hint);
            }

            .demo_visual {
              position: relative;
              width: 80px;
              height: 48px;
              background: rgba(0, 0, 0, 0.02);
              border-radius: 8px;
              overflow: hidden;

              .demo_key {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 24px;
                height: 24px;
                background: #fff;
                border: 1.5px solid #eee;
                border-radius: 4px;
                animation: key-flash 2s infinite;
              }

              .demo_keys {
                display: flex;
                gap: 4px;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                .mini_key {
                  width: 14px;
                  height: 14px;
                  background: #fff;
                  border: 1.5px solid #eee;
                  border-radius: 3px;
                  animation: key-highlight 2s infinite;

                  &:nth-child(2) {
                    animation-delay: 0.2s;
                  }
                  &:nth-child(3) {
                    animation-delay: 0.4s;
                  }
                }
              }

              .demo_hand {
                position: absolute;
                width: 28px;
                height: 28px;
                color: var(--brand-color-pure);
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
                z-index: 2;

                &.click_anim {
                  animation: demo-click 2s infinite ease-out;
                }

                &.drag_anim {
                  animation: demo-drag 2s infinite ease-out;
                }
              }
            }
          }
        }

        .text_area {
          text-align: center;

          .main_tip {
            font-size: 15px;
            font-weight: 600;
            color: var(--color-dark);
          }
        }
      }
    }

    @keyframes demo-click {
      0%,
      20% {
        transform: translate(28px, 20px) scale(1);
        opacity: 0;
      }
      30% {
        transform: translate(28px, 16px) scale(0.9);
        opacity: 1;
      }
      50% {
        transform: translate(28px, 16px) scale(0.85);
        opacity: 1;
      }
      70%,
      100% {
        transform: translate(28px, 16px) scale(1);
        opacity: 0;
      }
    }

    @keyframes demo-drag {
      0% {
        transform: translate(15px, 20px) scale(0.85);
        opacity: 0;
      }
      15% {
        opacity: 1;
      }
      70% {
        transform: translate(45px, 20px) scale(0.85);
        opacity: 1;
      }
      85%,
      100% {
        opacity: 0;
      }
    }
    @keyframes key-flash {
      0%,
      20% {
        background: #fff;
        border-color: #eee;
      }
      30%,
      60% {
        background: rgba(var(--color-brand-1-rgb), 0.2);
        border-color: var(--brand-color-pure);
      }
      70%,
      100% {
        background: #fff;
        border-color: #eee;
      }
    }
    @keyframes key-highlight {
      0%,
      20% {
        background: #fff;
        border-color: #eee;
        transform: scale(1);
      }
      30%,
      60% {
        background: rgba(var(--color-brand-1-rgb), 0.2);
        border-color: var(--brand-color-pure);
        transform: scale(1.05);
      }
      70%,
      100% {
        background: #fff;
        border-color: #eee;
        transform: scale(1);
      }
    }
  }

  .device > * {
    max-width: 100%;
  }
}

/* 引入共用的 fade-slide 动画 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s var(--transition-base);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, -100%) scale(0.9);
}
</style>
